import React, { useState } from 'react';
import { Card, Button, Alert, Spinner, Modal, Toast } from 'react-bootstrap';
import { Download, AlertCircle, Info, CheckCircle2 } from 'lucide-react';
import useAuthHeaders from '../../hooks/useAuthHeaders';
import RequestViewer from './RequestViewer';

const API_URL_V2 = process.env.REACT_APP_API_URL_V2;

const ImssOcTab = ({ credentials }) => {
  const { getToken } = useAuthHeaders();

  const isReady = !!credentials.rfc;

  const [loading, setLoading]         = useState(false);
  const [showToast, setShowToast]     = useState(false);
  const [errorMsg, setErrorMsg]       = useState('');
  const [showError, setShowError]     = useState(false);
  const [lastRequest, setLastRequest] = useState(null);

  const downloadPdf = (blob) => {
    const url  = URL.createObjectURL(blob);
    const link = Object.assign(document.createElement('a'), {
      href: url,
      download: `opinion-cumplimiento-imss-${credentials.rfc}-${new Date().toISOString().slice(0, 10)}.pdf`,
    });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isReady) return;
    setLoading(true);

    try {
      const token = await getToken();
      const res = await fetch(`${API_URL_V2}/Consultar/imssoc`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Rfc': credentials.rfc,
        },
      });

      if (!res.ok) {
        const ct  = res.headers.get('content-type') || '';
        const msg = ct.includes('application/json')
          ? ((await res.json()).errorMessage || `Error ${res.status}`)
          : (await res.text() || `Error ${res.status}`);
        throw new Error(msg);
      }

      const blob = await res.blob();
      downloadPdf(blob);
      setShowToast(true);

      setLastRequest({
        method:      'GET',
        baseUrl:     `${API_URL_V2}/Consultar/imssoc`,
        queryParams: null,
        headers:     { 'Rfc': credentials.rfc },
        formFields:  null,
        jsonBody:    null,
        jsPrefix:    null,
      });

    } catch (err) {
      setErrorMsg(err.message || 'Error al obtener la Opinión de Cumplimiento IMSS.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card className="tab-section-card border-0 shadow-sm">
        <Card.Body>
          <Alert variant="info" className="d-flex align-items-start gap-2 mb-4">
            <Info size={18} className="flex-shrink-0 mt-1" />
            <div>
              <strong>Opinión de Cumplimiento IMSS</strong>
              <div className="mt-1 small">
                Descarga directamente del portal del IMSS la opinión de cumplimiento del RFC.
                Solo requiere el RFC — no necesita CIEC ni FIEL. Se descargará como PDF automáticamente.
              </div>
            </div>
          </Alert>

          {!isReady && (
            <Alert variant="warning" className="d-flex align-items-center py-2 mb-3">
              <AlertCircle size={16} className="me-2 flex-shrink-0" />
              <small>Selecciona un RFC arriba para habilitar la descarga.</small>
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Button type="submit" className="btn-gradient" disabled={!isReady || loading}>
              {loading
                ? <><Spinner animation="border" size="sm" className="me-2" />Generando PDF…</>
                : <><Download size={18} />Generar Opinión de Cumplimiento IMSS</>
              }
            </Button>
          </form>

          <RequestViewer request={lastRequest} />
        </Card.Body>
      </Card>

      <Toast show={showToast} onClose={() => setShowToast(false)} delay={4000} autohide
        className="custom-toast toast-success">
        <Toast.Header>
          <CheckCircle2 size={16} className="me-2 text-success" />
          <strong className="me-auto">PDF generado</strong>
        </Toast.Header>
        <Toast.Body>La Opinión de Cumplimiento IMSS se descargó correctamente.</Toast.Body>
      </Toast>

      <Modal show={showError} onHide={() => setShowError(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '3px solid #ff6b6b' }}>
          <Modal.Title className="d-flex align-items-center gap-2" style={{ color: '#1a1f3a' }}>
            <AlertCircle size={20} color="#ff6b6b" />Error al generar el PDF
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0" style={{ color: '#4a5568', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {errorMsg}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowError(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ImssOcTab;
